import React, { useEffect, useState } from 'react';
import { Link, useParams } from 'react-router-dom';
import { Calendar, User, Eye, ArrowLeft } from 'lucide-react';
import apiService from '../services/api';
import { BlogPost } from '../types';
import { SEO } from '../components/SEO';

export const BlogPostPage: React.FC = () => {
  const { slug } = useParams<{ slug: string }>();
  const [post, setPost] = useState<BlogPost | null>(null);
  const [loading, setLoading] = useState(true);
  const [notFound, setNotFound] = useState(false);

  useEffect(() => {
    if (!slug) return;
    setLoading(true);
    setNotFound(false);
    apiService
      .getBlogPostBySlug(slug)
      .then((response) => {
        if (response.success && response.data) {
          setPost(response.data);
        } else {
          setNotFound(true);
        }
      })
      .catch(() => setNotFound(true))
      .finally(() => setLoading(false));
  }, [slug]);

  const formatDate = (dateString?: string) => {
    if (!dateString) return '';
    return new Date(dateString).toLocaleDateString('en-IN', { year: 'numeric', month: 'long', day: 'numeric' });
  };

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-primary"></div>
      </div>
    );
  }

  if (notFound || !post) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <div className="text-center">
          <p className="text-gray-600 text-lg mb-4">Blog post not found.</p>
          <Link to="/blog" className="text-primary font-medium hover:underline">Back to Blog</Link>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-white">
      <SEO
        title={post.seo_title || post.title}
        description={post.seo_description || post.excerpt || post.title}
        keywords={post.seo_keywords}
        path={`/blog/${post.slug}`}
        image={post.cover_image ? apiService.getImageUrl(post.cover_image) : undefined}
        type="article"
        structuredData={{
          '@context': 'https://schema.org',
          '@type': 'BlogPosting',
          headline: post.title,
          description: post.excerpt,
          author: { '@type': 'Organization', name: post.author || 'AARAN HOMES Team' },
          datePublished: post.published_at || post.created_at,
          dateModified: post.updated_at,
          image: post.cover_image ? apiService.getImageUrl(post.cover_image) : undefined,
          mainEntityOfPage: `https://aaranhome.com/blog/${post.slug}`,
        }}
      />

      {post.cover_image && (
        <div className="w-full h-64 sm:h-80 lg:h-96 bg-gray-100 overflow-hidden">
          <img src={apiService.getImageUrl(post.cover_image)} alt={post.title} className="w-full h-full object-cover" />
        </div>
      )}

      <div className="container mx-auto px-4 py-10 max-w-3xl">
        <Link to="/blog" className="inline-flex items-center gap-1 text-primary hover:underline mb-6 text-sm font-medium">
          <ArrowLeft className="w-4 h-4" /> Back to Blog
        </Link>

        <h1 className="text-3xl sm:text-4xl font-bold text-gray-900 mb-4 leading-tight">{post.title}</h1>

        <div className="flex items-center flex-wrap gap-4 text-sm text-gray-500 mb-8 pb-8 border-b border-gray-100">
          <span className="flex items-center gap-1">
            <Calendar className="w-4 h-4" /> {formatDate(post.published_at || post.created_at)}
          </span>
          <span className="flex items-center gap-1">
            <User className="w-4 h-4" /> {post.author || 'AARAN HOMES Team'}
          </span>
          <span className="flex items-center gap-1">
            <Eye className="w-4 h-4" /> {post.views || 0} views
          </span>
        </div>

        <div
          className="prose prose-lg max-w-none prose-headings:text-gray-900 prose-a:text-primary"
          dangerouslySetInnerHTML={{ __html: post.content }}
        />

        <div className="mt-12 p-6 bg-gray-50 rounded-2xl text-center">
          <p className="text-gray-700 mb-4">Looking for your own farmhouse, plot or second home?</p>
          <Link
            to="/properties"
            className="inline-block bg-primary text-white px-6 py-3 rounded-lg font-semibold hover:bg-primary/90 transition-colors"
          >
            Browse Properties
          </Link>
        </div>
      </div>
    </div>
  );
};

export default BlogPostPage;
